<!-- HomeworkCreationForm.vue -->
<template>
	<div class="homework-creation-form">
		<div class="homework-creation-form__container">
			<div v-if="isOffline" class="form-offline-banner" role="status">
				Нет интернета. Сохранение будет поставлено в очередь ({{ pendingQueueCount }}).
			</div>
			<!-- Настройки домашнего задания -->
			<div class="homework-settings">
				<div class="homework-settings__header">
					<div class="homework-settings__header-title">
						{{
							isEditMode
								? 'Редактирование домашнего задания'
								: 'Создание домашнего задания'
						}}
					</div>
					<button
						class="homework-settings__close-button"
						type="button"
						@click="handleClose"
						aria-label="Закрыть"
					>
						<img
							class="homework-settings__close-icon"
							alt="Закрыть"
							src="@/assets/cdn-animaapp/vector-8.svg"
						/>
					</button>
				</div>

				<!-- Название задания -->
				<div class="homework-settings__field">
					<label class="homework-settings__label">
						Название задания
						<span class="homework-required-mark" aria-hidden="true">*</span>
					</label>
					<input
						v-model="homeworkTitle"
						type="text"
						class="homework-settings__input"
						:class="{
							'homework-settings__input--error': validationErrors.title,
						}"
						placeholder="Введите название задания"
					/>
					<p v-if="validationErrors.title" class="homework-settings__error">
						{{ validationErrors.title }}
					</p>
				</div>

				<!-- Индикатор автосохранения -->
				<div v-if="lastSaveTimeFormatted" class="homework-settings__autosave">
					<span class="homework-settings__autosave-text">
						Последнее сохранение: {{ lastSaveTimeFormatted }}
					</span>
				</div>

				<!-- Срок сдачи -->
				<div class="homework-settings__deadline-section">
					<h3 class="homework-settings__section-title">Срок сдачи</h3>
					<div class="homework-settings__deadline-row">
						<div class="homework-settings__deadline-field">
							<label class="homework-settings__label">
								Дата
							</label>
							<DateTimePicker
								v-model="dueDate"
								mode="date"
								:min-date="minDate"
								:error="!!validationErrors.dueDate"
								placeholder="Выберите дату"
							/>
							<p
								v-if="validationErrors.dueDate"
								class="homework-settings__error"
							>
								{{ validationErrors.dueDate }}
							</p>
						</div>
						<div class="homework-settings__deadline-field">
							<label class="homework-settings__label">Время</label>
							<DateTimePicker
								v-model="dueTime"
								mode="time"
								:minute-step="5"
								:disabled="!hasDueDate"
								placeholder="Выберите время"
							/>
							<p v-if="validationErrors.dueTime" class="homework-settings__error">
								{{ validationErrors.dueTime }}
							</p>
						</div>
					</div>
					<p v-if="validationErrors.publishDate" class="homework-settings__error">
						{{ validationErrors.publishDate }}
					</p>

					<!-- Разрешить опоздание -->
					<div class="homework-settings__checkbox-wrapper">
						<label
							class="homework-settings__checkbox-label"
							:class="{
								'homework-settings__checkbox-label--disabled':
									!canUseLateSubmission,
							}"
						>
							<input
								v-model="allowLateSubmission"
								type="checkbox"
								class="homework-settings__checkbox"
								:disabled="!canUseLateSubmission"
							/>
							<span class="homework-settings__checkbox-custom">
								<svg
									class="homework-settings__checkbox-icon"
									viewBox="0 0 24 24"
									width="12"
									height="12"
									fill="none"
									aria-hidden="true"
								>
									<path
										d="M5 13l4 4L19 7"
										stroke="currentColor"
										stroke-width="2.5"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</span>
							<span class="homework-settings__checkbox-text"
								>Разрешить сдачу с опозданием</span
							>
						</label>
						<p class="homework-settings__hint">
							Студенты смогут сдать работу после дедлайна
						</p>
					</div>
				</div>

				<!-- Максимальный балл -->
				<div class="homework-settings__score-section">
					<h3 class="homework-settings__section-title">Оценка</h3>
					<div class="homework-settings__score-row">
						<div class="homework-settings__score-field">
							<label class="homework-settings__label">Максимальный балл</label>
							<input
								v-model.number="maxScore"
								type="number"
								min="0"
								max="1000"
								class="homework-settings__input homework-settings__input--score"
								placeholder="100"
							/>
						</div>
						<div class="homework-settings__score-info">
							<span class="homework-settings__score-icon" aria-hidden="true">
								<svg viewBox="0 0 24 24" width="16" height="16" fill="none">
									<path
										d="M4 19h16"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
									/>
									<rect
										x="6"
										y="11"
										width="3"
										height="6"
										rx="1"
										stroke="currentColor"
										stroke-width="2"
									/>
									<rect
										x="11"
										y="7"
										width="3"
										height="10"
										rx="1"
										stroke="currentColor"
										stroke-width="2"
									/>
									<rect
										x="16"
										y="4"
										width="3"
										height="13"
										rx="1"
										stroke="currentColor"
										stroke-width="2"
									/>
								</svg>
							</span>
							<span class="homework-settings__score-text">
								Оставьте пустым, если оценка не требуется
							</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Содержание задания -->
			<div class="homework-content">
				<h3 class="homework-content__title">Содержание задания</h3>

				<!-- Описание задания -->
				<div class="homework-section homework-section--description">
					<h4 class="homework-section__title">
						Описание задания
						<span class="homework-required-mark" aria-hidden="true">*</span>
					</h4>
					<div class="homework-section__card">
						<TopicTextEditor
							v-model="homeworkDescription"
							:error="!!validationErrors.description"
							editor-class="homework-content__textarea"
							placeholder="Опишите, что нужно сделать студентам..."
							:min-height="140"
						/>
						<p
							v-if="validationErrors.description"
							class="homework-content__error"
						>
							{{ validationErrors.description }}
						</p>
					</div>
				</div>

				<!-- Требования к выполнению -->
				<div class="homework-section homework-section--requirements">
					<h4 class="homework-section__title">
						Требования к выполнению
						<span class="homework-required-mark" aria-hidden="true">*</span>
					</h4>
					<div class="homework-section__card">
						<div class="homework-requirements">
							<div
								v-for="(requirement, index) in requirements"
								:key="index"
								class="homework-requirement"
							>
								<span class="homework-requirement__number"
									>{{ index + 1 }}.</span
								>
								<input
									v-model="requirements[index]"
									type="text"
									class="homework-requirement__input"
									:placeholder="`Введите требование ${index + 1}`"
								/>
								<button
									v-if="requirements.length > 1"
									type="button"
									class="homework-requirement__remove"
									@click="removeRequirement(index)"
									title="Удалить требование"
								>
									<svg viewBox="0 0 24 24" width="12" height="12" fill="none">
										<path
											d="M6 6l12 12M18 6l-12 12"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
										/>
									</svg>
								</button>
							</div>
						</div>
						<button
							type="button"
							class="homework-requirements__add-btn"
							@click="addRequirement"
						>
							<span class="homework-requirements__add-icon">+</span>
							<span class="homework-requirements__add-text"
								>Добавить требование</span
							>
						</button>
						<p v-if="validationErrors.requirements" class="homework-content__error">
							{{ validationErrors.requirements }}
						</p>
					</div>
				</div>

				<!-- Дополнительные материалы -->
				<div class="homework-section homework-section--materials">
					<h4 class="homework-section__title">Дополнительные материалы</h4>
					<div class="homework-section__card homework-section__card--upload">
						<div class="homework-content__field">
							<div class="homework-materials-upload">
								<input
									ref="homeworkMaterialsFileInput"
									type="file"
									multiple
									@change="handleMaterialsUpload"
									class="homework-content__file-input"
									id="homeworkMaterialsFileInput"
								/>
								<label
									for="homeworkMaterialsFileInput"
									class="homework-materials-upload__label"
								>
									<span class="homework-materials-upload__icon">
										<svg
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
												fill="white"
											/>
											<path
												d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
												fill="white"
											/>
										</svg>
									</span>
									<span class="homework-materials-upload__text"
										>Загрузить файлы</span
									>
								</label>
							</div>
							<div class="homework-materials-upload__hint">
								<span class="homework-materials-upload__description">
									Добавьте инструкции, шаблоны или примеры
								</span>
								<br />
								<span class="homework-materials-upload__size-hint">
									Максимальный размер каждого файла:
									{{ MATERIAL_MAX_SIZE_MB }} МБ
								</span>
							</div>
						</div>
						<p v-if="materialsError" class="homework-content__error">
							{{ materialsError }}
						</p>

						<!-- Прогресс загрузки -->
						<div v-if="isUploading" class="homework-upload-progress">
							<div class="homework-upload-progress__bar">
								<div
									class="homework-upload-progress__fill"
									:style="{ width: uploadProgress + '%' }"
								></div>
							</div>
							<span class="homework-upload-progress__text">
								Загрузка... {{ uploadProgress }}%
							</span>
						</div>

						<!-- Список загруженных файлов -->
						<div v-if="materials.length" class="homework-materials-list">
							<div
								v-for="(file, index) in materials"
								:key="index"
								class="homework-material-item"
							>
								<div
									class="homework-material-item__icon"
									:class="`homework-material-item__icon--${getFileIconType(file)}`"
								>
									<svg
										class="homework-file-icon"
										viewBox="0 0 24 24"
										width="20"
										height="20"
										fill="none"
										aria-hidden="true"
									>
										<path
											d="M7 2h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
											stroke="currentColor"
											stroke-width="2"
										/>
										<path
											d="M14 2v5h5"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
										/>
									</svg>
								</div>
								<div class="homework-material-item__info">
									<span class="homework-material-item__name">
										{{ file.name || file.file_name }}
									</span>
									<span class="homework-material-item__size">
										{{ formatFileSize(file.size || file.file_size || 0) }}
									</span>
									<span
										v-if="isNewFile(file)"
										class="homework-material-item__badge"
									>
										Новый
									</span>
								</div>
								<button
									type="button"
									class="homework-material-item__remove"
									@click="removeMaterial(index)"
									title="Удалить файл"
								>
									<svg viewBox="0 0 24 24" width="10" height="10" fill="none">
										<path
											d="M6 6l12 12M18 6l-12 12"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
										/>
									</svg>
								</button>
							</div>
						</div>
					</div>
				</div>

				<!-- Кнопки действий -->
				<div class="homework-actions">
					<span
						class="homework-actions__preview-tooltip"
						:title="homeworkPreviewDisabledHint"
					>
						<button
							type="button"
							class="homework-actions__button homework-actions__button--ghost"
							:disabled="!canOpenHomeworkPreview"
							@click="openHomeworkPreviewInNewTab"
						>
							Предпросмотр
						</button>
					</span>
					<button
						v-if="false"
						type="button"
						class="homework-actions__button homework-actions__button--secondary"
						@click="handleSaveDraft"
					>
						Сохранить как черновик
					</button>

					<div class="homework-actions__primary-group">
						<button
							type="button"
							class="homework-actions__button homework-actions__button--primary"
							:class="{
								'homework-actions__button--disabled': isSaveButtonDisabled,
							}"
							:disabled="isSaveButtonDisabled || isSaving"
							@click="handleSaveHomework"
						>
							<span v-if="isSaving" class="homework-actions__spinner"></span>
							{{ saveButtonText }}
						</button>
						<div
							v-if="getHomeworkSubmitErrorMessages().length"
							class="homework-actions__errors"
						>
							<p
								v-for="(message, index) in getHomeworkSubmitErrorMessages()"
								:key="`${message}-${index}`"
								class="homework-actions__error"
							>
								{{ message }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Модальное окно предпросмотра -->
		<div
			v-if="showPreviewModal"
			class="preview-modal"
			@click.self="handleClosePreview"
		>
			<div class="preview-modal__content">
				<div class="preview-modal__header">
					<h3 class="preview-modal__title">Предпросмотр домашнего задания</h3>
					<button
						type="button"
						class="preview-modal__close"
						@click="handleClosePreview"
						title="Закрыть"
					>
						<svg viewBox="0 0 24 24" width="14" height="14" fill="none">
							<path
								d="M6 6l12 12M18 6l-12 12"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
							/>
						</svg>
					</button>
				</div>

				<!-- Вкладки предпросмотра -->
				<div class="preview-modal__tabs">
					<button
						type="button"
						class="preview-modal__tab"
						:class="{ 'preview-modal__tab--active': previewTab === 'content' }"
						@click="previewTab = 'content'"
					>
						Содержание
					</button>
					<button
						type="button"
						class="preview-modal__tab"
						:class="{ 'preview-modal__tab--active': previewTab === 'settings' }"
						@click="previewTab = 'settings'"
					>
						Настройки
					</button>
					<button
						type="button"
						class="preview-modal__tab"
						:class="{
							'preview-modal__tab--active': previewTab === 'materials',
						}"
						@click="previewTab = 'materials'"
					>
						Материалы
						<span v-if="materials.length" class="preview-modal__tab-badge">
							{{ materials.length }}
						</span>
					</button>
				</div>

				<div class="preview-modal__body">
					<!-- Вкладка "Содержание" -->
					<div v-if="previewTab === 'content'" class="preview-tab-content">
						<div class="preview-section">
							<div class="preview-section__header">
								<h4 class="preview-section__title">
									{{ homeworkTitle || 'Без названия' }}
								</h4>
								<span class="preview-section__type">
									<svg
										class="preview-section__type-icon"
										viewBox="0 0 24 24"
										width="14"
										height="14"
										fill="none"
										aria-hidden="true"
									>
										<path
											d="M7 3h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
											stroke="currentColor"
											stroke-width="2"
										/>
										<path
											d="M14 3v5h5"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
										/>
										<path
											d="M8 13h8M8 17h6"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
										/>
									</svg>
									<span>Домашнее задание</span>
								</span>
							</div>
						</div>

						<div
							v-if="hasMeaningfulText(homeworkDescription)"
							class="preview-section"
						>
							<h5 class="preview-section__subtitle">Описание</h5>
							<div
								class="preview-section__text"
								v-html="sanitizePreviewHtml(homeworkDescription)"
							></div>
						</div>

						<div v-if="filteredRequirements.length > 0" class="preview-section">
							<h5 class="preview-section__subtitle">Требования к выполнению</h5>
							<ul class="preview-requirements">
								<li
									v-for="(req, index) in filteredRequirements"
									:key="index"
									class="preview-requirement"
								>
									<span class="preview-requirement__number">{{
										index + 1
									}}</span>
									<span class="preview-requirement__text">{{ req }}</span>
								</li>
							</ul>
						</div>
					</div>

					<!-- Вкладка "Настройки" -->
					<div v-if="previewTab === 'settings'" class="preview-tab-content">
						<div class="preview-section">
							<h5 class="preview-section__subtitle">Срок сдачи</h5>
							<div class="preview-info-card">
								<div class="preview-info-card__row">
									<span class="preview-info-card__icon" aria-hidden="true">
										<svg viewBox="0 0 24 24" width="16" height="16" fill="none">
											<rect
												x="3"
												y="4"
												width="18"
												height="18"
												rx="2"
												stroke="currentColor"
												stroke-width="2"
											/>
											<line
												x1="3"
												y1="10"
												x2="21"
												y2="10"
												stroke="currentColor"
												stroke-width="2"
											/>
											<line
												x1="8"
												y1="2"
												x2="8"
												y2="6"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
											<line
												x1="16"
												y1="2"
												x2="16"
												y2="6"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
										</svg>
									</span>
									<span class="preview-info-card__label">Дата:</span>
									<span class="preview-info-card__value">
										{{ formatDate(dueDate) || 'Не указана' }}
									</span>
								</div>
								<div v-if="dueTime" class="preview-info-card__row">
									<span class="preview-info-card__icon" aria-hidden="true">
										<svg viewBox="0 0 24 24" width="16" height="16" fill="none">
											<circle
												cx="12"
												cy="12"
												r="9"
												stroke="currentColor"
												stroke-width="2"
											/>
											<path
												d="M12 7v5l3 3"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
										</svg>
									</span>
									<span class="preview-info-card__label">Время:</span>
									<span class="preview-info-card__value">{{ dueTime }}</span>
								</div>
								<div class="preview-info-card__row">
									<span class="preview-info-card__icon" aria-hidden="true">
										<svg
											v-if="allowLateSubmission"
											viewBox="0 0 24 24"
											width="16"
											height="16"
											fill="none"
										>
											<path
												d="M5 13l4 4L19 7"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
										<svg
											v-else
											viewBox="0 0 24 24"
											width="16"
											height="16"
											fill="none"
										>
											<path
												d="M6 6l12 12M18 6l-12 12"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
										</svg>
									</span>
									<span class="preview-info-card__value">
										{{
											allowLateSubmission
												? 'Разрешена сдача с опозданием'
												: 'Строгий дедлайн'
										}}
									</span>
								</div>
							</div>
						</div>

						<div v-if="maxScore" class="preview-section">
							<h5 class="preview-section__subtitle">Оценка</h5>
							<div class="preview-info-card">
								<div class="preview-info-card__row">
									<span class="preview-info-card__icon" aria-hidden="true">
										<svg viewBox="0 0 24 24" width="16" height="16" fill="none">
											<path
												d="M4 19h16"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
											<rect
												x="6"
												y="11"
												width="3"
												height="6"
												rx="1"
												stroke="currentColor"
												stroke-width="2"
											/>
											<rect
												x="11"
												y="7"
												width="3"
												height="10"
												rx="1"
												stroke="currentColor"
												stroke-width="2"
											/>
											<rect
												x="16"
												y="4"
												width="3"
												height="13"
												rx="1"
												stroke="currentColor"
												stroke-width="2"
											/>
										</svg>
									</span>
									<span class="preview-info-card__label"
										>Максимальный балл:</span
									>
									<span
										class="preview-info-card__value preview-info-card__value--highlight"
									>
										{{ maxScore }}
									</span>
								</div>
							</div>
						</div>

						<div
							v-if="isScheduledPublish && publishDate"
							class="preview-section"
						>
							<h5 class="preview-section__subtitle">Публикация</h5>
							<div class="preview-info-card">
								<div class="preview-info-card__row">
									<span class="preview-info-card__icon" aria-hidden="true">
										<svg viewBox="0 0 24 24" width="16" height="16" fill="none">
											<rect
												x="3"
												y="4"
												width="18"
												height="18"
												rx="2"
												stroke="currentColor"
												stroke-width="2"
											/>
											<line
												x1="3"
												y1="10"
												x2="21"
												y2="10"
												stroke="currentColor"
												stroke-width="2"
											/>
											<path
												d="M8 14h8M8 18h5"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
										</svg>
									</span>
									<span class="preview-info-card__value">
										Запланирована на {{ formatDate(publishDate) }} в
										{{ publishTime || '00:00' }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Вкладка "Материалы" -->
					<div v-if="previewTab === 'materials'" class="preview-tab-content">
						<div v-if="materials.length > 0" class="preview-section">
							<h5 class="preview-section__subtitle">Прикрепленные файлы</h5>
							<div class="preview-materials">
								<div
									v-for="(file, index) in materials"
									:key="index"
									class="preview-material"
								>
									<span
										class="preview-material__icon"
										:class="`preview-material__icon--${getFileIconType(file)}`"
									>
										<svg
											class="homework-file-icon"
											viewBox="0 0 24 24"
											width="18"
											height="18"
											fill="none"
											aria-hidden="true"
										>
											<path
												d="M7 2h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
												stroke="currentColor"
												stroke-width="2"
											/>
											<path
												d="M14 2v5h5"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
										</svg>
									</span>
									<div class="preview-material__info">
										<span class="preview-material__name">{{
											file.name || file.file_name
										}}</span>
										<span class="preview-material__size">
											{{ formatFileSize(file.size || file.file_size || 0) }}
										</span>
									</div>
								</div>
							</div>
						</div>
						<div v-else class="preview-empty">
							<span class="preview-empty__icon" aria-hidden="true">
								<svg viewBox="0 0 24 24" width="36" height="36" fill="none">
									<path
										d="M7 12l7-7a4 4 0 0 1 6 6l-9 9a5 5 0 1 1-7-7l8-8"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</span>
							<p class="preview-empty__text">Материалы не прикреплены</p>
						</div>
					</div>
				</div>

				<div class="preview-modal__footer">
					<button
						type="button"
						class="preview-modal__button preview-modal__button--secondary"
						@click="handleClosePreview"
					>
						Закрыть
					</button>

					<button
						type="button"
						class="preview-modal__button preview-modal__button--primary"
						@click="handleSaveAndClosePreview"
					>
						{{ isEditMode ? 'Применить изменения' : 'Создать задание' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AutoPublishSettings from './test-creation/AutoPublishSettings.vue'
import DateTimePicker from '@/components/ui/DateTimePicker.vue'
import DOMPurify from 'dompurify'
import TopicTextEditor from '../../../../components/ProjectCreateCourse/common/TopicTextEditor.vue'
import { openInternalRouteInContext } from '@/utils/bitrixInternalNavigation'

const MATERIAL_MAX_SIZE_MB = 50
const MATERIAL_MAX_SIZE_BYTES = MATERIAL_MAX_SIZE_MB * 1024 * 1024

export default {
	name: 'HomeworkCreationForm',
	components: {
		AutoPublishSettings,
		DateTimePicker,
		TopicTextEditor,
	},
	props: {
		currentModuleId: {
			type: [String, Number],
			default: null,
		},
		availableModules: {
			type: Array,
			default: () => [],
		},
		editingHomework: {
			type: Object,
			default: null,
		},
	},
	emits: ['save-homework', 'save-draft', 'cancel'],
	data() {
		return {
			// Редактирование
			editingHomeworkId: null,
			isEditingDraft: false, // Флаг: редактируем черновик

			// Основные поля
			homeworkTitle: '',
			homeworkDescription: '',
			selectedModule: '',

			// Срок сдачи
			dueDate: '',
			dueTime: '',
			allowLateSubmission: false,

			// Оценка
			maxScore: null,

			// Публикация
			isScheduledPublish: false,
			publishDate: '',
			publishTime: '',

			// Требования
			requirements: [''],

			// Материалы
			materials: [],
			materialsError: '',
			deletedMaterials: [],

			// Загрузка
			isUploading: false,
			uploadProgress: 0,
			isSaving: false,

			// Автосохранение
			autoSaveInterval: null,
			lastSaveTime: null,
			storageKey: 'homework_form_autosave',

			// Предпросмотр
			showPreviewModal: false,
			previewTab: 'content',

			// Валидация
			validationErrors: {},

			// Флаг сохранения (для предотвращения ложных предупреждений)
			isSaved: false,
			// НОВЫЕ ПОЛЯ
			isLoadingData: false, // Флаг загрузки данных (блокирует watchers)
			originalData: null, // Снимок исходного состояния

			// Константы
			MATERIAL_MAX_SIZE_MB,
			isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
			pendingQueueCount: 0,
			submitQueueKey: 'homework_form_submit_queue_v1',
		}
	},
	computed: {
		isEditMode() {
			return this.editingHomeworkId !== null
		},
		lastSaveTimeFormatted() {
			if (!this.lastSaveTime) return null
			const date = new Date(this.lastSaveTime)
			return date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
		},
		minDate() {
			return this.getLocalDateString(new Date())
		},
		hasDueDate() {
			return !!this.normalizeDateValue(this.dueDate)
		},
		hasDueDateTime() {
			return this.hasDueDate && !!this.normalizeTimeValue(this.dueTime)
		},
		canUseLateSubmission() {
			return this.hasDueDateTime
		},
		filteredRequirements() {
			return this.requirements.filter(r => r.trim())
		},
		formData() {
			const normalizedDueDate = this.normalizeDateValue(this.dueDate)
			const normalizedDueTime = normalizedDueDate
				? this.normalizeTimeValue(this.dueTime)
				: ''
			const normalizedMaxScore = this.normalizeMaxScoreValue(this.maxScore)
			return {
				homeworkTitle: this.homeworkTitle,
				homeworkDescription: this.homeworkDescription,
				selectedModule: this.selectedModule,
				dueDate: normalizedDueDate,
				dueTime: normalizedDueTime,
				maxScore: normalizedMaxScore,
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				allowLateSubmission:
					normalizedDueDate && normalizedDueTime
						? this.allowLateSubmission
						: false,
				requirements: this.requirements,
			}
		},
		// НОВЫЕ computed свойства
		saveButtonText() {
			if (this.isEditMode) {
				const hasChanges = this.hasUnsavedChanges()
				return hasChanges ? 'Применить изменения' : 'Сохранено'
			} else {
				return 'Создать задание'
			}
		},
		isSaveButtonDisabled() {
			if (this.isEditMode) {
				return !this.hasUnsavedChanges()
			}
			return false
		},
		canOpenHomeworkPreview() {
			const moduleId = this.currentModuleId || this.selectedModule
			return (
				moduleId !== null &&
				moduleId !== undefined &&
				moduleId !== '' &&
				this.editingHomeworkId !== null &&
				this.editingHomeworkId !== undefined &&
				this.editingHomeworkId !== '' &&
				!String(moduleId).startsWith('temp_') &&
				!String(this.editingHomeworkId).startsWith('temp_')
			)
		},
		homeworkPreviewDisabledHint() {
			return this.canOpenHomeworkPreview
				? ''
				: 'Сначала создайте домашнее задание, чтобы открыть предпросмотр.'
		},
	},
	watch: {
		editingHomework: {
			handler(newHomework) {
				if (newHomework) {
					this.loadEditingData()
				} else {
					this.resetForm()
				}
			},
			immediate: true,
			deep: false,
		},
		// ОБНОВЛЕННЫЕ watchers с проверкой isLoadingData
		homeworkTitle() {
			if (!this.isLoadingData) {
				this.saveFormData()
				this.clearValidationError('title')
				this.isSaved = false
			}
		},
		homeworkDescription() {
			if (!this.isLoadingData) {
				this.saveFormData()
				this.clearValidationError('description')
				this.isSaved = false
			}
		},
		dueDate() {
			if (!this.isLoadingData) {
				if (!this.hasDueDate && this.dueTime) {
					this.dueTime = ''
				}
				if (!this.hasDueDate && this.allowLateSubmission) {
					this.allowLateSubmission = false
				}
				this.saveFormData()
				this.clearValidationError('dueDate')
				this.isSaved = false
			}
		},
		dueTime() {
			if (!this.isLoadingData) {
				if (!this.hasDueDate && this.dueTime) {
					this.dueTime = ''
					return
				}
				if (!this.canUseLateSubmission && this.allowLateSubmission) {
					this.allowLateSubmission = false
				}
				this.saveFormData()
				this.isSaved = false
			}
		},
		maxScore() {
			if (!this.isLoadingData) {
				this.saveFormData()
				this.isSaved = false
			}
		},
		isScheduledPublish() {
			if (!this.isLoadingData) {
				this.saveFormData()
				this.isSaved = false
			}
		},
		publishDate() {
			if (!this.isLoadingData) {
				this.saveFormData()
				this.isSaved = false
			}
		},
		publishTime() {
			if (!this.isLoadingData) {
				this.saveFormData()
				this.isSaved = false
			}
		},
		allowLateSubmission() {
			if (!this.isLoadingData) {
				if (this.allowLateSubmission && !this.canUseLateSubmission) {
					this.allowLateSubmission = false
					return
				}
				this.saveFormData()
				this.isSaved = false
			}
		},
		materials: {
			handler() {
				if (!this.isLoadingData) {
					this.isSaved = false
				}
			},
			deep: true,
		},
		requirements: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
			deep: true,
		},
		currentModuleId(newId, oldId) {
			if (newId && newId !== oldId && newId >= 1000000000000) {
				this.clearLocalStorage()
			}
		},
	},
	mounted() {
		if (this.currentModuleId && !this.editingHomework) {
			this.selectedModule = this.currentModuleId
		}

		if (this.editingHomework) {
			this.loadEditingData()
		} else {
			const isNewModule =
				this.currentModuleId && this.currentModuleId >= 1000000000000
			if (isNewModule) {
				this.clearLocalStorage()
			} else {
				this.restoreFromLocalStorage()
			}
		}

		this.startAutoSave()
		this.pendingQueueCount = this.getSubmitQueue().length
		window.addEventListener('online', this.handleOnline)
		window.addEventListener('offline', this.handleOffline)
		if (!this.isOffline) {
			this.flushSubmitQueue()
		}
	},
	beforeUnmount() {
		this.stopAutoSave()
		window.removeEventListener('online', this.handleOnline)
		window.removeEventListener('offline', this.handleOffline)
	},
	methods: {
		getHomeworkSubmitErrorMessages() {
			const messages = []
			if (this.materialsError) {
				messages.push(this.materialsError)
			}
			const orderedFields = [
				'title',
				'description',
				'requirements',
				'dueDate',
				'dueTime',
				'publishDate',
			]
			for (const field of orderedFields) {
				const message = this.validationErrors?.[field]
				if (message) {
					messages.push(message)
				}
			}
			return [...new Set(messages)]
		},
		getHomeworkSubmitErrorMessage() {
			return this.getHomeworkSubmitErrorMessages()[0] || ''
		},
		handleOnline() {
			this.isOffline = false
			this.flushSubmitQueue()
		},
		handleOffline() {
			this.isOffline = true
		},
		getSubmitQueue() {
			try {
				const raw = localStorage.getItem(this.submitQueueKey)
				const parsed = raw ? JSON.parse(raw) : []
				return Array.isArray(parsed) ? parsed : []
			} catch (error) {
				console.error('Ошибка чтения очереди отправки ДЗ:', error)
				return []
			}
		},
		setSubmitQueue(queue) {
			try {
				localStorage.setItem(this.submitQueueKey, JSON.stringify(queue))
			} catch (error) {
				console.error('Ошибка записи очереди отправки ДЗ:', error)
			}
			this.pendingQueueCount = Array.isArray(queue) ? queue.length : 0
		},
		enqueueSubmit(eventName, payload) {
			const queue = this.getSubmitQueue()
			queue.push({ eventName, payload, createdAt: Date.now() })
			this.setSubmitQueue(queue)
		},
		flushSubmitQueue() {
			if (typeof navigator !== 'undefined' && !navigator.onLine) return
			const queue = this.getSubmitQueue()
			if (!queue.length) return
			queue.forEach(item => {
				if (item && item.eventName && item.payload) {
					this.$emit(item.eventName, item.payload)
				}
			})
			this.setSubmitQueue([])
		},
		parseDateTime(dateValue, timeValue, asEndOfDay = false) {
			const date = this.parseLocalDate(dateValue)
			if (!date) return null
			const time = this.normalizeTimeValue(timeValue || '')
			if (time) {
				const [hours, minutes] = time.split(':').map(Number)
				date.setHours(hours || 0, minutes || 0, 0, 0)
			} else if (asEndOfDay) {
				date.setHours(23, 59, 59, 999)
			} else {
				date.setHours(0, 0, 0, 0)
			}
			return date
		},
		// ============================================
		// НОВЫЕ МЕТОДЫ для отслеживания изменений
		// ============================================

		/**
		 * Захватывает текущее состояние формы как исходное
		 */
		captureOriginalData() {
			const normalizedMaxScore = this.normalizeMaxScoreValue(this.maxScore)
			this.originalData = {
				homeworkTitle: this.homeworkTitle,
				homeworkDescription: this.homeworkDescription,
				dueDate: this.dueDate,
				dueTime: this.dueTime,
				maxScore: normalizedMaxScore,
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				allowLateSubmission: this.allowLateSubmission,
				requirements: JSON.stringify(this.requirements),
				// Для файлов сохраняем только имена для сравнения
				materialFileNames: this.materials.map(m => m.name || m.file_name || ''),
			}
			console.log('Снимок исходных данных домашки:', this.originalData)
		},
		handleSaveAndClosePreview() {
			this.handleClosePreview()
			this.handleSaveHomework()
		},
		/**
		 * Проверяет, есть ли реальные изменения относительно исходных данных
		 */
		hasUnsavedChanges() {
			// Если форма была успешно сохранена — изменений нет
			if (this.isSaved) {
				return false
			}

			// Если нет снимка исходных данных — значит это создание нового задания
			// Проверяем, заполнено ли хоть что-то
			if (!this.originalData) {
				const normalizedMaxScore = this.normalizeMaxScoreValue(this.maxScore)
				return (
					this.homeworkTitle ||
					this.homeworkDescription ||
					this.dueDate ||
					normalizedMaxScore !== null ||
					this.materials.length > 0 ||
					this.requirements.some(r => r && r.trim())
				)
			}

			// Сравниваем текущее состояние с исходным
			const normalizedMaxScore = this.normalizeMaxScoreValue(this.maxScore)
			const current = {
				homeworkTitle: this.homeworkTitle,
				homeworkDescription: this.homeworkDescription,
				dueDate: this.dueDate,
				dueTime: this.dueTime,
				maxScore: normalizedMaxScore,
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				allowLateSubmission: this.allowLateSubmission,
				requirements: JSON.stringify(this.requirements),
				materialFileNames: this.materials.map(m => m.name || m.file_name || ''),
			}

			// Проверяем каждое поле
			const changes = {
				homeworkTitle:
					current.homeworkTitle !== this.originalData.homeworkTitle,
				homeworkDescription:
					current.homeworkDescription !== this.originalData.homeworkDescription,
				dueDate: current.dueDate !== this.originalData.dueDate,
				dueTime: current.dueTime !== this.originalData.dueTime,
				maxScore: current.maxScore !== this.originalData.maxScore,
				isScheduledPublish:
					current.isScheduledPublish !== this.originalData.isScheduledPublish,
				publishDate: current.publishDate !== this.originalData.publishDate,
				publishTime: current.publishTime !== this.originalData.publishTime,
				allowLateSubmission:
					current.allowLateSubmission !== this.originalData.allowLateSubmission,
				requirements: current.requirements !== this.originalData.requirements,
				materials:
					JSON.stringify(current.materialFileNames) !==
					JSON.stringify(this.originalData.materialFileNames),
			}

			const hasChanges = Object.values(changes).some(changed => changed)

			if (hasChanges) {
				console.log('Обнаружены изменения в домашке:', changes)
			}

			return hasChanges
		},

		// ============================================
		// ЗАГРУЗКА И СБРОС ДАННЫХ
		// ============================================
		loadEditingData() {
			const data = this.editingHomework
			if (!data) return

			// Блокируем watchers на время загрузки
			this.isLoadingData = true

			console.log('Загрузка данных для редактирования домашки:', data)

			this.editingHomeworkId = data.id || null
			this.isEditingDraft = data.isDraft === true || data.is_draft === true
			this.homeworkTitle = data.title || ''
			this.homeworkDescription = data.description || ''
			this.selectedModule = data.module || this.currentModuleId || ''
			this.dueDate = this.normalizeDateValue(
				data.dueDate || data.due_date || ''
			)
			this.dueTime = this.normalizeTimeValue(
				data.dueTime || data.due_time || ''
			)
			this.maxScore = this.normalizeMaxScoreValue(
				data.maxScore ?? data.max_score ?? null
			)
			this.isScheduledPublish =
				data.publishType === 'scheduled' || data.publish_type === 'scheduled'
			this.publishDate = data.publishDate || data.publish_date || ''
			this.publishTime = data.publishTime || data.publish_time || ''
			this.allowLateSubmission =
				data.allowLateSubmission ?? data.allow_late_submission ?? false
			if (!this.hasDueDate) {
				this.dueTime = ''
			}
			if (!this.hasDueDateTime) {
				this.allowLateSubmission = false
			}

			// Требования - парсим JSON если это строка (из БД)
			let parsedRequirements = data.requirements
			if (typeof parsedRequirements === 'string') {
				try {
					parsedRequirements = JSON.parse(parsedRequirements)
				} catch (e) {
					console.warn('Ошибка парсинга requirements:', e)
					parsedRequirements = []
				}
			}
			this.requirements =
				Array.isArray(parsedRequirements) && parsedRequirements.length > 0
					? [...parsedRequirements]
					: ['']

			// Материалы - парсим JSON если это строка (из БД)
			let parsedMaterials = data.materials
			if (typeof parsedMaterials === 'string') {
				try {
					parsedMaterials = JSON.parse(parsedMaterials)
				} catch (e) {
					console.warn('Ошибка парсинга materials:', e)
					parsedMaterials = []
				}
			}
			if (Array.isArray(parsedMaterials) && parsedMaterials.length > 0) {
				this.materials = [...parsedMaterials]
			} else {
				this.materials = []
			}

			this.deletedMaterials = []
			this.materialsError = ''
			this.validationErrors = {}

			console.log('Данные домашки загружены:', {
				title: this.homeworkTitle,
				description: this.homeworkDescription,
				dueDate: this.dueDate,
				requirements: this.requirements.length,
				materials: this.materials.length,
			})

			// Сохраняем снимок исходного состояния
			this.captureOriginalData()

			// Разблокируем watchers
			this.isLoadingData = false
			this.isSaved = true // Данные загружены, изменений пока нет
		},

		resetForm() {
			this.editingHomeworkId = null
			this.isEditingDraft = false
			this.homeworkTitle = ''
			this.homeworkDescription = ''
			this.dueDate = ''
			this.dueTime = ''
			this.maxScore = null
			this.isScheduledPublish = false
			this.publishDate = ''
			this.publishTime = ''
			this.allowLateSubmission = false
			this.requirements = ['']
			this.materials = []
			this.materialsError = ''
			this.deletedMaterials = []
			this.validationErrors = {}
			this.showPreviewModal = false
			this.previewTab = 'content'
			this.lastSaveTime = null
			this.isSaved = false
			this.originalData = null // Сбросить снимок
			this.clearFormData()
		},

		// ============================================
		// ВАЛИДАЦИЯ
		// ============================================
		validateForm() {
			this.validationErrors = {}
			let isValid = true

			if (!this.homeworkTitle.trim()) {
				this.validationErrors.title = 'Введите название задания'
				isValid = false
			}

			if (!this.hasMeaningfulText(this.homeworkDescription)) {
				this.validationErrors.description = 'Введите описание задания'
				isValid = false
			}
			if (!this.requirements.some(r => r && r.trim())) {
				this.validationErrors.requirements =
					'Добавьте хотя бы одно требование к выполнению'
				isValid = false
			}

			if (this.dueDate) {
				const selectedDate = this.parseLocalDate(this.dueDate)
				if (!selectedDate) {
					this.validationErrors.dueDate = 'Некорректная дата'
					isValid = false
				} else {
					const today = new Date()
					today.setHours(0, 0, 0, 0)

					if (selectedDate < today) {
					this.validationErrors.dueDate = 'Дата сдачи не может быть в прошлом'
					isValid = false
					}
				}
			}
			if (this.allowLateSubmission && !this.hasDueDateTime) {
				this.validationErrors.dueTime =
					'Для опции опоздания укажите дату и время сдачи'
				isValid = false
			}
			if (this.isScheduledPublish && this.publishDate && this.dueDate) {
				const publishAt = this.parseDateTime(this.publishDate, this.publishTime)
				const dueAt = this.parseDateTime(this.dueDate, this.dueTime, true)
				if (publishAt && dueAt && publishAt > dueAt) {
					this.validationErrors.publishDate =
						'Дата публикации не может быть позже дедлайна'
					isValid = false
				}
				if (publishAt && dueAt && this.publishDate === this.dueDate) {
					const publishTime = this.normalizeTimeValue(this.publishTime)
					const dueTime = this.normalizeTimeValue(this.dueTime)
					if (publishTime && dueTime && dueTime < publishTime) {
						this.validationErrors.dueTime =
							'Время окончания не может быть раньше времени начала'
						isValid = false
					}
				}
			}

			return isValid
		},

		clearValidationError(field) {
			if (this.validationErrors[field]) {
				delete this.validationErrors[field]
			}
		},

		// ============================================
		// ТРЕБОВАНИЯ
		// ============================================
		addRequirement() {
			this.requirements.push('')
		},

		removeRequirement(index) {
			if (!window.confirm('Удалить требование?')) {
				return
			}
			this.requirements.splice(index, 1)
		},

		// ============================================
		// МАТЕРИАЛЫ
		// ============================================
		handleMaterialsUpload(event) {
			console.log('handleMaterialsUpload вызван')
			const input = event.target
			const newFiles = Array.from(input.files || [])
			console.log(
				' Выбрано файлов:',
				newFiles.length,
				newFiles.map(f => f.name)
			)
			this.materialsError = ''

			if (!newFiles.length) {
				console.log('Нет файлов, выход')
				input.value = ''
				return
			}

			const validFiles = []
			const errors = []
			const maxFiles = 10

			if (this.materials.length + newFiles.length > maxFiles) {
				this.materialsError = `Можно загрузить не более ${maxFiles} файлов. Сейчас загружено: ${this.materials.length}.`
				input.value = ''
				return
			}

			// Симуляция прогресса загрузки
			this.isUploading = true
			this.uploadProgress = 0

			const progressInterval = setInterval(() => {
				if (this.uploadProgress < 90) {
					this.uploadProgress += 10
				}
			}, 100)

			for (const file of newFiles) {
				if (file.size && file.size > MATERIAL_MAX_SIZE_BYTES) {
					errors.push(
						`"${file.name}" слишком большой (максимум: ${MATERIAL_MAX_SIZE_MB} МБ)`
					)
					continue
				}

				const isDuplicate = this.materials.some(existingFile => {
					const existingName = existingFile.name || existingFile.file_name
					return existingName === file.name
				})

				if (isDuplicate) {
					errors.push(`"${file.name}" уже загружен`)
					continue
				}

				validFiles.push(file)
			}

			setTimeout(() => {
				clearInterval(progressInterval)
				this.uploadProgress = 100

				setTimeout(() => {
					this.isUploading = false
					this.uploadProgress = 0

					if (errors.length > 0) {
						this.materialsError = `Некоторые файлы не были загружены: ${errors.join(
							', '
						)}.`
					}

					if (validFiles.length > 0) {
						this.materials = [...this.materials, ...validFiles]
						console.log(
							' Материалы после добавления:',
							this.materials.length,
							this.materials.map(f => f.name || f.file_name)
						)
					}

					input.value = ''
				}, 300)
			}, 500)
		},

		removeMaterial(index) {
			if (!window.confirm('Удалить материал?')) {
				return
			}
			const material = this.materials[index]
			if (material && !this.isNewFile(material)) {
				this.deletedMaterials.push(material)
			}
			this.materials.splice(index, 1)
		},

		isNewFile(file) {
			return file instanceof File
		},

		getFileIconType(file) {
			const name = (file.name || file.file_name || '').toLowerCase()
			if (name.endsWith('.pdf')) return 'pdf'
			if (name.endsWith('.doc') || name.endsWith('.docx')) return 'doc'
			if (name.endsWith('.xls') || name.endsWith('.xlsx')) return 'sheet'
			if (name.endsWith('.ppt') || name.endsWith('.pptx')) return 'slides'
			if (name.endsWith('.zip') || name.endsWith('.rar')) return 'archive'
			if (name.match(/\.(jpg|jpeg|png|gif|webp)$/)) return 'image'
			if (name.match(/\.(mp4|avi|mov|mkv)$/)) return 'video'
			return 'file'
		},

		// ============================================
		// УТИЛИТЫ
		// ============================================
		getLocalDateString(date) {
			if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
				return ''
			}
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			return `${year}-${month}-${day}`
		},

		normalizeDateValue(value) {
			if (!value) return ''
			if (value instanceof Date) {
				return this.getLocalDateString(value)
			}
			if (typeof value !== 'string') return ''
			const trimmed = value.trim()
			const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)
			return match ? match[1] : ''
		},

		normalizeTimeValue(value) {
			if (!value) return ''
			if (value instanceof Date) {
				const hours = String(value.getHours()).padStart(2, '0')
				const minutes = String(value.getMinutes()).padStart(2, '0')
				return `${hours}:${minutes}`
			}
			if (typeof value !== 'string') return ''
			const match = value.match(/(\d{2}:\d{2})/)
			return match ? match[1] : ''
		},
		normalizeMaxScoreValue(value) {
			if (value === null || value === undefined || value === '') return null
			const numeric = Number(value)
			return Number.isFinite(numeric) ? numeric : null
		},

		parseLocalDate(value) {
			const normalized = this.normalizeDateValue(value)
			if (!normalized) return null
			const [year, month, day] = normalized.split('-').map(Number)
			if (!year || !month || !day) return null
			return new Date(year, month - 1, day)
		},

		formatFileSize(bytes) {
			if (!bytes) return '0 Bytes'
			const k = 1024
			const sizes = ['Bytes', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
		},

		formatDate(dateString) {
			if (!dateString) return null
			const date = this.parseLocalDate(dateString)
			if (!date) return null
			return date.toLocaleDateString('ru-RU', {
				day: 'numeric',
				month: 'long',
				year: 'numeric',
			})
		},

		// ============================================
		// АВТОСОХРАНЕНИЕ
		// ============================================
		saveFormData() {
			if (this.editingHomeworkId) return

			try {
				const dataToSave = {
					...this.formData,
					timestamp: Date.now(),
				}
				localStorage.setItem(this.storageKey, JSON.stringify(dataToSave))
				this.lastSaveTime = Date.now()
			} catch (error) {
				console.error('Ошибка автосохранения:', error)
			}
		},

		restoreFromLocalStorage() {
			try {
				const saved = localStorage.getItem(this.storageKey)
				if (saved) {
					const data = JSON.parse(saved)
					const dayInMs = 24 * 60 * 60 * 1000

					if (Date.now() - data.timestamp < dayInMs) {
						this.homeworkTitle = data.homeworkTitle || ''
						this.homeworkDescription = data.homeworkDescription || ''
						this.selectedModule =
							data.selectedModule || this.currentModuleId || ''
						this.dueDate = this.normalizeDateValue(data.dueDate || '')
						this.dueTime = this.normalizeTimeValue(data.dueTime || '')
						this.maxScore = this.normalizeMaxScoreValue(data.maxScore ?? null)
						this.isScheduledPublish = data.isScheduledPublish ?? false
						this.publishDate = data.publishDate || ''
						this.publishTime = data.publishTime || ''
						this.allowLateSubmission = data.allowLateSubmission ?? false
						if (!this.hasDueDate) {
							this.dueTime = ''
						}
						if (!this.hasDueDateTime) {
							this.allowLateSubmission = false
						}
						this.requirements = data.requirements || ['']
						this.lastSaveTime = data.timestamp
					} else {
						localStorage.removeItem(this.storageKey)
					}
				}
			} catch (error) {
				console.error('Ошибка восстановления данных:', error)
			}
		},

		clearLocalStorage() {
			try {
				localStorage.removeItem(this.storageKey)
				this.lastSaveTime = null
			} catch (error) {
				console.error('Ошибка очистки localStorage:', error)
			}
		},

		clearFormData() {
			this.clearLocalStorage()
		},

		startAutoSave() {
			this.autoSaveInterval = setInterval(() => {
				this.saveFormData()
			}, 30000)
		},

		stopAutoSave() {
			if (this.autoSaveInterval) {
				clearInterval(this.autoSaveInterval)
				this.autoSaveInterval = null
			}
		},

		// ============================================
		// ДЕЙСТВИЯ
		// ============================================
		handleSaveHomework() {
			if (!this.validateForm()) {
				return
			}

			this.isSaving = true
			const normalizedDueDate = this.normalizeDateValue(this.dueDate)
			const normalizedDueTime = normalizedDueDate
				? this.normalizeTimeValue(this.dueTime)
				: ''
			const normalizedMaxScore = this.normalizeMaxScoreValue(this.maxScore)
			const normalizedAllowLateSubmission =
				normalizedDueDate && normalizedDueTime
					? this.allowLateSubmission
					: false

			const homeworkData = {
				...(this.editingHomeworkId && { id: this.editingHomeworkId }),
				title: this.homeworkTitle,
				description: this.homeworkDescription,
				dueDate: normalizedDueDate,
				dueTime: normalizedDueTime,
				maxScore: normalizedMaxScore,
				module: this.selectedModule,
				publishType: this.isScheduledPublish ? 'scheduled' : 'immediate',
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				allowLateSubmission: normalizedAllowLateSubmission,
				requirements: this.filteredRequirements,
				materials: this.materials,
				deletedMaterials: this.deletedMaterials,
				// Сохраняем статус черновика при редактировании
				isDraft: this.isEditingDraft,
				is_draft: this.isEditingDraft,
			}

			setTimeout(() => {
				this.isSaved = true
				if (typeof navigator !== 'undefined' && !navigator.onLine) {
					this.enqueueSubmit('save-homework', homeworkData)
					alert('Нет интернета. Сохранение ДЗ поставлено в очередь.')
					this.isSaving = false
					return
				}
				this.$emit('save-homework', homeworkData)
				this.clearLocalStorage()
				this.isSaving = false
			}, 300)
		},

		handleSaveDraft() {
			if (this.isSaving) return
			this.isSaving = true
			const normalizedDueDate = this.normalizeDateValue(this.dueDate)
			const normalizedDueTime = normalizedDueDate
				? this.normalizeTimeValue(this.dueTime)
				: ''
			const normalizedMaxScore = this.normalizeMaxScoreValue(this.maxScore)
			const normalizedAllowLateSubmission =
				normalizedDueDate && normalizedDueTime
					? this.allowLateSubmission
					: false
			const draftData = {
				...(this.editingHomeworkId && { id: this.editingHomeworkId }),
				title: this.homeworkTitle,
				description: this.homeworkDescription,
				dueDate: normalizedDueDate,
				dueTime: normalizedDueTime,
				maxScore: normalizedMaxScore,
				module: this.selectedModule,
				publishType: this.isScheduledPublish ? 'scheduled' : 'immediate',
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				allowLateSubmission: normalizedAllowLateSubmission,
				requirements: this.filteredRequirements,
				materials: this.materials,
				deletedMaterials: this.deletedMaterials,
				isDraft: true,
				is_draft: true,
			}
			this.isSaved = true
			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				this.enqueueSubmit('save-draft', draftData)
				alert('Нет интернета. Сохранение черновика поставлено в очередь.')
				this.isSaving = false
				return
			}
			this.$emit('save-draft', draftData)
			this.clearLocalStorage()
			setTimeout(() => {
				this.isSaving = false
			}, 300)
		},

		handlePreview() {
			if (
				!this.homeworkTitle &&
				!this.hasMeaningfulText(this.homeworkDescription)
			) {
				this.materialsError =
					'Заполните хотя бы название или описание для предпросмотра.'
				return
			}
			this.previewTab = 'content'
			this.showPreviewModal = true
		},
		sanitizePreviewHtml(html = '') {
			return DOMPurify.sanitize(html || '', {
				ALLOWED_TAGS: [
					'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins',
					'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
					'blockquote', 'code', 'pre', 'hr', 'figure', 'figcaption', 'img',
					'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
					'colgroup', 'col', 'div', 'span', 'label', 'input', 'small',
					'mark', 'sub', 'sup', 'oembed',
				],
				ALLOWED_ATTR: [
					'href', 'target', 'rel', 'src', 'srcset', 'sizes', 'alt', 'title',
					'class', 'id', 'style', 'width', 'height', 'colspan', 'rowspan',
					'type', 'checked', 'disabled', 'value', 'name', 'url', 'start',
					'reversed', 'dir', 'lang', 'aria-label',
				],
				ALLOW_DATA_ATTR: true,
				FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
				FORBID_ATTR: ['on*'],
			})
		},
		getPlainTextFromHtml(html = '') {
			if (!html) return ''
			const container = document.createElement('div')
			container.innerHTML = html
			return (container.textContent || '').replace(/\u00A0/g, ' ').trim()
		},
		hasMeaningfulText(html = '') {
			const raw = String(html || '')
			if (!raw.trim()) return false
			if (/<(img|table|ul|ol|blockquote|pre|hr|input|oembed|figure)\b/i.test(raw)) {
				return true
			}
			return this.getPlainTextFromHtml(raw).length > 0
		},
		openHomeworkPreviewInNewTab() {
			if (!this.canOpenHomeworkPreview) {
				alert('Сначала сохраните модуль и домашнее задание, затем откройте предпросмотр.')
				return
			}

			const moduleId = this.currentModuleId || this.selectedModule
			openInternalRouteInContext(this.$router, {
				name: 'TeacherScreen8Preview',
				query: {
					moduleId: String(moduleId),
					previewType: 'module',
					section: 'homework',
					homeworkId: String(this.editingHomeworkId),
					testMode: '1',
				},
			}, {
				title: 'Предпросмотр домашнего задания',
				leftBoundary: 48,
			})
		},

		handleClosePreview() {
			this.showPreviewModal = false
		},

		handleClose() {
			const hasRealChanges = this.hasUnsavedChanges()

			if (hasRealChanges) {
				const confirmed = window.confirm(
					'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть форму?'
				)
				if (!confirmed) return
			}

			// Сохраняем ID перед очисткой
			const scrollToId = this.editingHomeworkId

			this.clearFormData()

			// Передаём ID для скролла
			this.$emit('cancel', { scrollToId, type: 'homework' })
		},
	},
}
</script>

<style scoped>
.form-offline-banner {
	margin-bottom: 12px;
	padding: 10px 12px;
	border: 1px solid #f59e0b;
	border-radius: 8px;
	background: #fffbeb;
	color: #92400e;
	font-size: 13px;
	font-weight: 500;
}

.homework-actions__button--disabled {
	opacity: 0.5;
	cursor: not-allowed;
	background-color: #9ca3af !important;
	border: none !important;
	box-shadow: none !important;
}
.homework-actions__button--primary:not(
		.homework-actions__button--disabled
	):hover {
	opacity: 0.9;
	transform: translateY(-1px);
	transition: all 0.2s;
}

/* ============================================
   CSS ПЕРЕМЕННЫЕ
   ============================================ */
:root {
	--primary-color: #0095ec;
	--primary-light: #e6f3ff;
	--primary-dark: #0080cb;
	--border-color: #d1d5db;
	--border-light: #e5e7eb;
	--text-primary: #111827;
	--text-secondary: #4b5563;
	--text-muted: #6b7280;
	--text-placeholder: #9ca3af;
	--background-light: #f3f4f6;
	--error-color: #e11d48;
	--success-color: #059669;
	--border-radius: 6px;
	--transition: all 0.15s ease;
}

/* ============================================
   ОСНОВНОЙ КОНТЕЙНЕР
   ============================================ */
.homework-creation-form {
	font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
		sans-serif;
	min-height: 100vh;
	color: #1f2933;
	box-sizing: border-box;
}

.homework-creation-form__container {
	position: relative;
	max-width: 980px;
	margin: 0 auto;
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
	padding: 24px 32px 0;
	box-sizing: border-box;
}

/* ============================================
   ШАПКА
   ============================================ */
.homework-settings {
	padding: 0;
	border-bottom: 1px solid #e5e7eb;
	margin-bottom: 24px;
}

.homework-settings__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
	margin-left: -32px;
	margin-top: -24px;
	width: calc(100% + 64px);
	background: #bd480099;
	padding: 16px 20px;
	border-radius: 12px 12px 0 0;
	position: relative;
}

.homework-settings__header-title {
	font-size: 18px;
	font-weight: 500;
	color: #ffffff;
	margin: 0;
}

.homework-settings__close-button {
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: opacity 0.15s ease;
	color: #ffffff;
}

.homework-settings__close-button:hover {
	opacity: 0.8;
}

.homework-settings__close-icon {
	width: 18px;
	height: 18px;
	filter: brightness(0) invert(1);
}

/* ============================================
   ПОЛЯ ВВОДА
   ============================================ */
.homework-settings__field {
	margin-bottom: 16px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.homework-settings__label {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 15px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 8px 0;
}

.homework-required-mark {
	color: #e11d48;
	font-weight: 700;
	margin-left: 4px;
}

.homework-settings__input {
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 10px 12px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
	width: 100%;
	box-sizing: border-box;
}

.homework-settings__input::placeholder {
	color: #9ca3af;
}

.homework-settings__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.homework-settings__input.homework-settings__input--error {
	border-color: #e11d48;
}

.homework-settings__input.homework-settings__input--error:focus {
	box-shadow: 0 0 0 2px rgba(225, 29, 72, 0.15);
}

.homework-settings__input--score {
	max-width: 120px;
}

.homework-settings__error {
	font-size: 12px;
	color: #e11d48;
	margin: 4px 0 0 0;
}

.homework-settings__hint {
	font-size: 12px;
	color: #9ca3af;
	margin: 4px 0 0 0;
}

/* ============================================
   АВТОСОХРАНЕНИЕ
   ============================================ */
.homework-settings__autosave {
	margin-top: -6px;
	margin-bottom: 12px;
}

.homework-settings__autosave-text {
	font-size: 11px;
	color: #9ca3af;
}

/* ============================================
   СЕКЦИЯ СРОКА СДАЧИ
   ============================================ */
.homework-settings__deadline-section,
.homework-settings__score-section {
	margin-top: 20px;
	margin-bottom: 20px;
}

.homework-settings__section-title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 17px;
	font-weight: 500;
	margin: 0 0 15px 0;
}

.homework-settings__deadline-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	margin-bottom: 16px;
}

.homework-settings__deadline-field {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 12px 12px 14px;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
	background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
	box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}


/* ============================================
   ЧЕКБОКС
   ============================================ */
.homework-settings__checkbox-wrapper {
	margin-top: 12px;
}

.homework-settings__checkbox-label {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	user-select: none;
}

.homework-settings__checkbox-label--disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.homework-settings__checkbox {
	position: absolute;
	opacity: 0;
	width: 0;
	height: 0;
}

.homework-settings__checkbox-custom {
	width: 20px;
	height: 20px;
	border: 2px solid #d1d5db;
	border-radius: 4px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: all 0.15s ease;
	flex-shrink: 0;
}

.homework-settings__checkbox:checked + .homework-settings__checkbox-custom {
	background: #0095ec;
	border-color: #0095ec;
}

.homework-settings__checkbox-icon {
	color: #ffffff;
	opacity: 0;
	transform: scale(0.6);
	transition: opacity 0.15s ease, transform 0.15s ease;
}

.homework-settings__checkbox:checked
	+ .homework-settings__checkbox-custom
	.homework-settings__checkbox-icon {
	opacity: 1;
	transform: scale(1);
}

.homework-settings__checkbox:focus + .homework-settings__checkbox-custom {
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.25);
}

.homework-settings__checkbox-text {
	font-size: 14px;
	color: #4b5563;
}

/* ============================================
   СЕКЦИЯ ОЦЕНКИ
   ============================================ */
.homework-settings__score-row {
	display: flex;
	align-items: flex-end;
	gap: 20px;
}

.homework-settings__score-field {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.homework-settings__score-info {
	display: flex;
	align-items: center;
	gap: 8px;
	padding-bottom: 10px;
}

.homework-settings__score-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #0095ec;
}

.homework-settings__score-text {
	font-size: 12px;
	color: #9ca3af;
}

/* ============================================
   СОДЕРЖАНИЕ ЗАДАНИЯ
   ============================================ */
.homework-content {
	padding: 0;
}

.homework-content__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 17px;
	font-weight: 500;
	margin: 0 0 20px 0;
}

/* ============================================
   СЕКЦИИ
   ============================================ */
.homework-section {
	margin-bottom: 24px;
}

.homework-section__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 15px;
	font-weight: 500;
	margin: 0 0 12px 0;
}

.homework-section__card {
	border-radius: 6px;
	border: 1px solid #e5e7eb;
	background: #ffffff;
	padding: 16px;
}

.homework-section__card--upload {
	background: #eaf5ff;
	border-color: #cfe3ff;
}

/* ============================================
   TEXTAREA
   ============================================ */
.homework-content__textarea {
	width: 100%;
	min-height: 120px;
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 12px;
	font-size: 14px;
	line-height: 1.6;
	resize: vertical;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
	box-sizing: border-box;
}

.homework-content__textarea::placeholder {
	color: #9ca3af;
}

.homework-content__textarea:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.homework-content__textarea--error {
	border-color: #e11d48;
}

.homework-content__error {
	font-size: 12px;
	color: #e11d48;
	margin: 8px 0 0 0;
}

/* Убираем внутренний "островок" редактора в карточке описания */
.homework-section--description :deep(.topic-text-editor__toolbar .ck-toolbar) {
	border: 0;
	border-radius: 0;
	background: transparent;
	padding: 0 0 8px;
	border-bottom: 1px solid #e5e7eb;
}

.homework-section--description :deep(.topic-text-editor__surface .ck.ck-editor__editable) {
	border: 0;
	border-radius: 0;
	padding: 12px 0 0;
}

.homework-section--description :deep(.topic-text-editor__surface .ck.ck-editor__editable.ck-focused) {
	border: 0;
	box-shadow: none;
}

/* ============================================
   ТРЕБОВАНИЯ
   ============================================ */
.homework-requirements {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-bottom: 12px;
}

.homework-requirement {
	display: flex;
	align-items: center;
	gap: 10px;
}

.homework-requirement__number {
	font-size: 14px;
	font-weight: 600;
	color: #6b7280;
	min-width: 24px;
}

.homework-requirement__input {
	flex: 1;
	padding: 10px 12px;
	background-color: #ffffff;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	color: #111827;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.homework-requirement__input::placeholder {
	color: #9ca3af;
}

.homework-requirement__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.homework-requirement__remove {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: none;
	background: #f3f4f6;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	color: #6b7280;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
}

.homework-requirement__remove:hover {
	background: #fee2e2;
	color: #b91c1c;
}

.homework-requirements__add-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 16px;
	background: transparent;
	color: #0095ec;
	border: 2px dashed #0095ec;
	border-radius: 6px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 500;
	transition: all 0.15s ease;
}

.homework-requirements__add-btn:hover {
	background: rgba(0, 149, 236, 0.05);
}

.homework-requirements__add-icon {
	font-size: 16px;
	font-weight: 600;
}

/* ============================================
   ЗАГРУЗКА МАТЕРИАЛОВ
   ============================================ */
.homework-content__field {
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 12px;
}

.homework-content__file-input {
	display: none;
}

.homework-materials-upload__label {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	border-radius: 8px;
	background: #0095ec;
	color: #ffffff;
	font-size: 13px;
	font-weight: 500;
	padding: 10px 18px;
	cursor: pointer;
	border: none;
	transition: background 0.15s ease, box-shadow 0.15s ease;
}

.homework-materials-upload__label:hover {
	background: #0080cb;
	box-shadow: 0 6px 14px rgba(0, 149, 236, 0.35);
}

.homework-materials-upload__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.homework-materials-upload__hint {
	flex: 1;
}

.homework-materials-upload__description {
	font-size: 12px;
	color: #6b7280;
}

.homework-materials-upload__size-hint {
	font-size: 11px;
	color: #9ca3af;
}

/* ============================================
   ПРОГРЕСС ЗАГРУЗКИ
   ============================================ */
.homework-upload-progress {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
}

.homework-upload-progress__bar {
	flex: 1;
	height: 6px;
	background: #e5e7eb;
	border-radius: 3px;
	overflow: hidden;
}

.homework-upload-progress__fill {
	height: 100%;
	background: linear-gradient(90deg, #0095ec 0%, #00c6ff 100%);
	border-radius: 3px;
	transition: width 0.2s ease;
}

.homework-upload-progress__text {
	font-size: 12px;
	color: #6b7280;
	min-width: 100px;
}

/* ============================================
   СПИСОК МАТЕРИАЛОВ
   ============================================ */
.homework-materials-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.homework-material-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 12px;
	border-radius: 6px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.homework-material-item:hover {
	border-color: #d1d5db;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.homework-material-item__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #64748b;
	flex-shrink: 0;
}

.homework-file-icon {
	display: block;
}

.homework-material-item__icon--pdf,
.preview-material__icon--pdf {
	color: #ef4444;
}

.homework-material-item__icon--doc,
.preview-material__icon--doc {
	color: #2563eb;
}

.homework-material-item__icon--sheet,
.preview-material__icon--sheet {
	color: #16a34a;
}

.homework-material-item__icon--slides,
.preview-material__icon--slides {
	color: #f97316;
}

.homework-material-item__icon--archive,
.preview-material__icon--archive {
	color: #8b5cf6;
}

.homework-material-item__icon--image,
.preview-material__icon--image {
	color: #14b8a6;
}

.homework-material-item__icon--video,
.preview-material__icon--video {
	color: #ec4899;
}

.homework-material-item__info {
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1;
	min-width: 0;
}

.homework-material-item__name {
	font-size: 13px;
	font-weight: 500;
	color: #111827;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 280px;
}

.homework-material-item__size {
	font-size: 11px;
	color: #6b7280;
	flex-shrink: 0;
}

.homework-material-item__badge {
	font-size: 10px;
	text-transform: uppercase;
	color: #059669;
	background: #ecfdf3;
	border-radius: 999px;
	padding: 2px 8px;
	font-weight: 500;
	flex-shrink: 0;
}

.homework-material-item__remove {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	border: none;
	background: #f3f4f6;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	color: #6b7280;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
	flex-shrink: 0;
}

.homework-material-item__remove:hover {
	background: #fee2e2;
	color: #b91c1c;
}

/* ============================================
   КНОПКИ ДЕЙСТВИЙ
   ============================================ */
.homework-actions {
	margin-top: 28px;
	margin-left: -32px;
	margin-right: -32px;
	padding: 16px 32px;
	background: #f5f6f8;
	border-top: 1px solid #e5e7eb;
	display: flex;
	align-items: center;
	gap: 12px;
	border-radius: 0 0 12px 12px;
}

.homework-actions__primary-group {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
}

.homework-actions__errors {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
	max-width: 420px;
}

.homework-actions__error {
	margin: 0;
	font-size: 12px;
	line-height: 1.4;
	color: #e11d48;
	text-align: left;
}

.homework-actions__button {
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	padding: 10px 20px;
	border: 1px solid transparent;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.15s ease, border-color 0.15s ease,
		box-shadow 0.15s ease;
	display: inline-flex;
	align-items: center;
	gap: 8px;
}

.homework-actions__button--ghost {
	background: transparent;
	color: #4b5563;
	border-color: transparent;
	margin-right: auto;
}

.homework-actions__preview-tooltip {
	display: inline-flex;
	margin-right: auto;
}

.homework-actions__button--ghost:hover {
	background: #e5e7eb;
}

.homework-actions__button--secondary {
	background: #ffffff;
	color: #111827;
	border-color: #d1d5db;
}

.homework-actions__button--secondary:hover {
	background: #f9fafb;
	box-shadow: 0 4px 10px rgba(148, 163, 184, 0.25);
}

.homework-actions__button--primary {
	background: #0095ec;
	color: #ffffff;
	border-color: #0095ec;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.35);
}

.homework-actions__button--primary:hover {
	background: #0080cb;
}

.homework-actions__button--primary:disabled {
	opacity: 0.7;
	cursor: not-allowed;
}

.homework-actions__spinner {
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-top-color: white;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* ============================================
   МОДАЛЬНОЕ ОКНО ПРЕДПРОСМОТРА
   ============================================ */
.preview-modal {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
	backdrop-filter: blur(2px);
}

.preview-modal__content {
	background-color: #ffffff;
	border-radius: 12px;
	max-width: 700px;
	width: 100%;
	max-height: 85vh;
	display: flex;
	flex-direction: column;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
	overflow: hidden;
}

.preview-modal__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px 24px;
	border-bottom: 1px solid #e5e7eb;
	background: #f9fafb;
}

.preview-modal__title {
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin: 0;
}

.preview-modal__close {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 16px;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.15s ease;
}

.preview-modal__close:hover {
	background-color: #fee2e2;
	border-color: #fecaca;
	color: #b91c1c;
}

/* ============================================
   ВКЛАДКИ ПРЕДПРОСМОТРА
   ============================================ */
.preview-modal__tabs {
	display: flex;
	gap: 0;
	padding: 0 24px;
	background: #f9fafb;
	border-bottom: 1px solid #e5e7eb;
}

.preview-modal__tab {
	padding: 12px 20px;
	background: transparent;
	border: none;
	border-bottom: 2px solid transparent;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.15s ease;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-bottom: -1px;
}

.preview-modal__tab:hover {
	color: #111827;
}

.preview-modal__tab--active {
	color: #0095ec;
	border-bottom-color: #0095ec;
}

.preview-modal__tab-badge {
	background: #0095ec;
	color: white;
	font-size: 10px;
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 999px;
}

/* ============================================
   ТЕЛО ПРЕДПРОСМОТРА
   ============================================ */
.preview-modal__body {
	flex: 1;
	overflow-y: auto;
	padding: 24px;
}

.preview-tab-content {
	animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(8px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.preview-section {
	margin-bottom: 24px;
}

.preview-section:last-child {
	margin-bottom: 0;
}

.preview-section__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 8px;
}

.preview-section__title {
	font-size: 22px;
	font-weight: 600;
	color: #111827;
	margin: 0;
	line-height: 1.3;
}

.preview-section__type {
	display: inline-block;
	padding: 4px 10px;
	background-color: rgba(0, 149, 236, 0.1);
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	color: #0095ec;
	white-space: nowrap;
	align-items: center;
	gap: 6px;
	display: inline-flex;
}

.preview-section__type-icon {
	flex-shrink: 0;
}

.preview-section__subtitle {
	font-size: 14px;
	font-weight: 600;
	color: #0095ec;
	margin: 0 0 10px 0;
}

.preview-section__text {
	font-size: 14px;
	line-height: 1.6;
	color: #4b5563;
	margin: 0;
}

/* ============================================
   ИНФОРМАЦИОННАЯ КАРТОЧКА
   ============================================ */
.preview-info-card {
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.preview-info-card__row {
	display: flex;
	align-items: center;
	gap: 10px;
}

.preview-info-card__icon {
	width: 24px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #0095ec;
}

.preview-info-card__label {
	font-size: 13px;
	color: #6b7280;
}

.preview-info-card__value {
	font-size: 14px;
	color: #111827;
	font-weight: 500;
}

.preview-info-card__value--highlight {
	color: #0095ec;
	font-size: 18px;
	font-weight: 600;
}

/* ============================================
   ТРЕБОВАНИЯ В ПРЕДПРОСМОТРЕ
   ============================================ */
.preview-requirements {
	list-style: none;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.preview-requirement {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 12px 16px;
	background-color: #f9fafb;
	border-left: 3px solid #0095ec;
	border-radius: 0 6px 6px 0;
}

.preview-requirement__number {
	width: 24px;
	height: 24px;
	background: #0095ec;
	color: white;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	flex-shrink: 0;
}

.preview-requirement__text {
	font-size: 14px;
	color: #4b5563;
	line-height: 1.5;
}

/* ============================================
   МАТЕРИАЛЫ В ПРЕДПРОСМОТРЕ
   ============================================ */
.preview-materials {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.preview-material {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
}

.preview-material__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #64748b;
}

.preview-material__info {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.preview-material__name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
}

.preview-material__size {
	font-size: 11px;
	color: #6b7280;
}

/* ============================================
   ПУСТОЕ СОСТОЯНИЕ
   ============================================ */
.preview-empty {
	text-align: center;
	padding: 40px 20px;
}

.preview-empty__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #94a3b8;
	opacity: 0.6;
}

.preview-empty__text {
	font-size: 14px;
	color: #9ca3af;
	margin: 12px 0 0 0;
}

/* ============================================
   ФУТЕР МОДАЛЬНОГО ОКНА
   ============================================ */
.preview-modal__footer {
	padding: 16px 24px;
	border-top: 1px solid #e5e7eb;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	background: #f9fafb;
}

.preview-modal__button {
	padding: 10px 24px;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
}

.preview-modal__button--secondary {
	background: #ffffff;
	color: #4b5563;
	border: 1px solid #d1d5db;
}

.preview-modal__button--secondary:hover {
	background: #f3f4f6;
}

.preview-modal__button--primary {
	background: #0095ec;
	color: #ffffff;
	border: none;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

.preview-modal__button--primary:hover {
	background: #0080cb;
}

/* ============================================
   АДАПТИВНОСТЬ
   ============================================ */
@media (max-width: 768px) {
	.homework-creation-form__container {
		margin: 0 12px;
		padding: 20px 16px 0;
	}

	.homework-settings__header {
		margin-left: -16px;
		width: calc(100% + 32px);
	}

	.homework-settings__deadline-row {
		grid-template-columns: 1fr;
	}

	.homework-settings__score-row {
		flex-direction: column;
		align-items: stretch;
		gap: 12px;
	}

	.homework-settings__score-info {
		padding-bottom: 0;
	}

	.homework-actions {
		margin-left: -16px;
		margin-right: -16px;
		padding: 12px 16px;
		flex-wrap: wrap;
	}

	.homework-actions__errors {
		width: 100%;
		max-width: none;
		align-items: flex-start;
	}

	.homework-actions__error {
		text-align: left;
	}

	.homework-actions__button--ghost {
		order: 3;
		width: 100%;
		margin-right: 0;
		margin-top: 8px;
	}

	.homework-actions__preview-tooltip {
		order: 3;
		width: 100%;
		margin-right: 0;
		margin-top: 8px;
	}

	.homework-actions__preview-tooltip .homework-actions__button--ghost {
		width: 100%;
		margin-right: 0;
		margin-top: 0;
	}

	.homework-content__field {
		flex-direction: column;
		align-items: flex-start;
	}

	.preview-modal__content {
		max-height: 90vh;
		margin: 0 10px;
	}

	.preview-modal__tabs {
		overflow-x: auto;
		padding: 0 16px;
	}

	.preview-modal__tab {
		padding: 10px 16px;
		font-size: 13px;
	}
}
</style>
